import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Users, Activity, Database } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, Legend, CartesianGrid,
} from 'recharts';
import { PageSEO } from '../components/seo/SchemaMarkup';
import { API } from '../lib/api';

const LIME = '#e8ff00';
const CYAN = '#00c8ff';
const ORANGE = '#ff7800';

function fmtHMS(sec) {
  if (!sec && sec !== 0) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.round(sec % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}
function fmtMS(sec) {
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

const SOURCES = [
  { name: 'Men’s Health UK — Average HYROX station times', url: 'https://www.menshealth.com/uk/fitness/a69165520/average-hyrox-station-times/' },
  { name: 'Women’s Health UK — HYROX station average times', url: 'https://www.womenshealthmag.com/uk/fitness/a69213266/hyrox-station-average-times/' },
  { name: 'HYROX Data Lab — What is a good HYROX time', url: 'https://hyroxdatalab.com/articles/what-is-a-good-hyrox-time' },
  { name: 'HYROX Data Lab — Station performance analysis', url: 'https://hyroxdatalab.com/articles/station-performance-analysis' },
  { name: 'Competitive.fit — HYROX information', url: 'https://www.competitive.fit/hyrox/information' },
];

function injectDatasetSchema(stats) {
  const id = 'hyrox-benchmark-dataset-jsonld';
  document.getElementById(id)?.remove();
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    name: 'NinjAthlete HYROX Finish-Time Benchmark Report 2026',
    description:
      'Aggregated HYROX finish-time and station-split benchmarks by division and gender, compiled from publicly available 2025 race results and NinjAthlete athlete submissions.',
    url: 'https://ninjathlete.com/hyrox/benchmark-report',
    creator: { '@type': 'Organization', name: 'NinjAthlete', url: 'https://ninjathlete.com' },
    license: 'https://creativecommons.org/licenses/by/4.0/',
    keywords: ['HYROX', 'HYROX finish time', 'HYROX benchmarks', 'HYROX average time', 'HYROX station splits'],
    temporalCoverage: '2025/2026',
    variableMeasured: ['Finish time', 'Station split time', 'Division', 'Gender', 'Percentile'],
  };
  const el = document.createElement('script');
  el.type = 'application/ld+json';
  el.id = id;
  el.text = JSON.stringify(schema);
  document.head.appendChild(el);
}

export default function HyroxBenchmarkReport() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetch(`${API}/hyrox/benchmark-stats`)
      .then((r) => r.json())
      .then((d) => { setStats(d); injectDatasetSchema(d); })
      .catch(() => {});
    return () => document.getElementById('hyrox-benchmark-dataset-jsonld')?.remove();
  }, []);

  const divisionData = stats
    ? Object.entries(stats.divisions).map(([key, d]) => ({
        key,
        name: d.label.replace(' — ', ' '),
        minutes: +(d.seed_mean_sec / 60).toFixed(1),
        avg_str: d.seed_avg_str,
      }))
    : [];

  const stationData = stats
    ? stats.station_splits.map((s) => ({
        name: s.label.replace(/ \d.*$/, ''),
        Men: +(s.men / 60).toFixed(2),
        Women: +(s.women / 60).toFixed(2),
        'Elite Men': +(s.elite_men / 60).toFixed(2),
      }))
    : [];

  return (
    <main className="pt-20 min-h-screen bg-[#0a0a0a] relative" data-testid="hyrox-benchmark-report">
      <PageSEO
        title="HYROX Finish-Time Benchmark Report 2026: Average Times & Station Splits | NinjAthlete"
        description="The NinjAthlete HYROX Benchmark Report: average finish times and station-by-station splits by division and gender, plus percentile tiers. Data-backed 2026 benchmarks."
        path="/hyrox/benchmark-report"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'HYROX', url: '/hyrox' },
          { name: 'Benchmark Report', url: '/hyrox/benchmark-report' },
        ]}
      />

      <div className="fixed inset-0 pointer-events-none z-0" style={{
        backgroundImage: 'linear-gradient(rgba(232,255,0,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(232,255,0,0.03) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
      }} />

      <div className="relative z-10 max-w-4xl mx-auto px-6 pb-24">
        <Link to="/hyrox" className="inline-flex items-center gap-2 text-zinc-500 hover:text-[#e8ff00] text-sm mb-8 transition-colors mt-4" data-testid="back-to-hyrox">
          <ArrowRight className="w-4 h-4 rotate-180" /> HYROX Hub
        </Link>

        {/* Hero */}
        <header className="mb-14">
          <p className="font-mono text-[10px] tracking-[0.25em] text-[#e8ff00] uppercase mb-4 flex items-center gap-2">
            <Database className="w-3.5 h-3.5" /> Original Data · Updated {new Date().getFullYear()}
          </p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[0.92] text-white mb-5" data-testid="report-title">
            THE HYROX <span className="text-[#e8ff00]">BENCHMARK<br />REPORT</span>
          </h1>
          <p className="text-base text-zinc-400 leading-relaxed max-w-2xl">
            How long does HYROX actually take? We analyzed finish times and station splits across
            divisions to build the definitive benchmark set — so you know exactly where you rank and
            where to find time.
          </p>
          <div className="grid grid-cols-3 gap-3 mt-8" data-testid="report-stat-cards">
            <StatCard icon={<Database className="w-4 h-4" />} value={stats ? `${Math.round(stats.total_analyzed / 1000)}k+` : '—'} label="Results Analyzed" />
            <StatCard icon={<Users className="w-4 h-4" />} value={stats ? `${stats.live_submissions}` : '—'} label="Athletes Submitted" />
            <StatCard icon={<Activity className="w-4 h-4" />} value="8" label="Divisions Tracked" />
          </div>
        </header>

        {/* Average finish time by division */}
        <Section title="Average Finish Time by Division" kicker="The headline numbers">
          <p className="text-zinc-400 text-sm leading-relaxed mb-6">
            Pro Men average <strong className="text-white">1:22:24</strong>; Open Women average
            <strong className="text-white"> 1:54:00</strong>. Across all divisions and ages, the typical
            finisher lands around <strong className="text-[#e8ff00]">90 minutes</strong>.
          </p>
          <div className="h-80 w-full" data-testid="division-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={divisionData} margin={{ top: 10, right: 10, left: -10, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: '#a1a1aa', fontSize: 11 }} angle={-25} textAnchor="end" interval={0} height={60} />
                <YAxis tick={{ fill: '#71717a', fontSize: 11 }} label={{ value: 'Minutes', angle: -90, position: 'insideLeft', fill: '#71717a', fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ background: '#161616', border: '1px solid #e8ff00', borderRadius: 0 }}
                  labelStyle={{ color: '#fff' }}
                  formatter={(v, n, p) => [p.payload.avg_str, 'Avg Time']}
                />
                <Bar dataKey="minutes" radius={[3, 3, 0, 0]}>
                  {divisionData.map((d) => (
                    <Cell key={d.key} fill={d.key.includes('pro') ? LIME : d.key.includes('masters') ? ORANGE : CYAN} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>

        {/* Percentile tiers */}
        <Section title="Where Do You Rank?" kicker="Performance tiers">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tiers-table">
              <thead>
                <tr className="border-b border-zinc-800">
                  {['Tier', 'Percentile', 'Men Finish', 'Women Finish'].map((h) => (
                    <th key={h} className="text-left font-mono text-[10px] tracking-[0.15em] text-[#e8ff00] uppercase px-3 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(stats?.tiers || []).map((t) => (
                  <tr key={t.tier} className="border-b border-zinc-800/60 hover:bg-[#e8ff0008]">
                    <td className="px-3 py-3 text-white font-semibold">{t.tier}</td>
                    <td className="px-3 py-3 text-zinc-400 font-mono">{t.badge}</td>
                    <td className="px-3 py-3 text-[#e8ff00] font-mono">{t.men_max ? `< ${fmtHMS(t.men_max)}` : 'Above median'}</td>
                    <td className="px-3 py-3 text-[#00c8ff] font-mono">{t.women_max ? `< ${fmtHMS(t.women_max)}` : 'Above median'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* Station splits */}
        <Section title="Station-by-Station Splits" kicker="Where the time goes">
          <p className="text-zinc-400 text-sm leading-relaxed mb-6">
            <strong className="text-white">Wall Balls</strong> are the single biggest separator between
            recreational and advanced athletes, followed by <strong className="text-white">RoxZone
            transitions</strong>. The <strong className="text-white">Sled Pull</strong> is
            disproportionately slow for average athletes versus elites.
          </p>
          <div className="h-96 w-full" data-testid="station-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stationData} margin={{ top: 10, right: 10, left: -10, bottom: 50 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: '#a1a1aa', fontSize: 10 }} angle={-30} textAnchor="end" interval={0} height={70} />
                <YAxis tick={{ fill: '#71717a', fontSize: 11 }} label={{ value: 'Minutes', angle: -90, position: 'insideLeft', fill: '#71717a', fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ background: '#161616', border: '1px solid #27272a', borderRadius: 0 }}
                  labelStyle={{ color: '#fff' }}
                  formatter={(v) => [`${fmtMS(v * 60)}`, '']}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: '#a1a1aa' }} />
                <Bar dataKey="Elite Men" fill={LIME} radius={[2, 2, 0, 0]} />
                <Bar dataKey="Men" fill={CYAN} radius={[2, 2, 0, 0]} />
                <Bar dataKey="Women" fill={ORANGE} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>

        {/* Key insights */}
        <Section title="Key Insights" kicker="What the data says">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="insights-grid">
            {[
              { h: 'Wall Balls decide races', p: 'Advanced men average 5:36 vs 8:02 for recreational — a 2:26 gap. It’s the #1 place to find time.' },
              { h: 'The RoxZone is free speed', p: 'Elite athletes keep transitions under ~21s each (~5:45 total). Beginners lose 9–12 minutes just walking.' },
              { h: 'Pro vs Open is closer than you think', p: 'Pro Women (1:27:03) finish only ~3 min faster than Open Men (1:26:42) despite heavier weights.' },
            ].map((i) => (
              <div key={i.h} className="bg-[#161616] border border-zinc-800 p-5">
                <TrendingUp className="w-5 h-5 text-[#e8ff00] mb-3" />
                <h3 className="text-white font-semibold text-sm mb-2">{i.h}</h3>
                <p className="text-zinc-500 text-xs leading-relaxed">{i.p}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* CTA */}
        <div className="bg-[#111] border border-[#e8ff00] p-8 sm:p-10 text-center my-12" data-testid="report-cta">
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-3">SEE WHERE <span className="text-[#e8ff00]">YOU</span> RANK</h2>
          <p className="text-zinc-400 text-sm mb-6 max-w-md mx-auto">Run your numbers through the Finish-Time Predictor and get your percentile against this benchmark set.</p>
          <Link to="/tools/hyrox-finish-time" className="inline-flex items-center gap-2 bg-[#e8ff00] text-black font-heading text-lg tracking-wider px-8 py-4 hover:bg-white transition-colors" data-testid="report-cta-btn">
            PREDICT MY FINISH TIME <ArrowRight className="w-5 h-5" />
          </Link>
        </div>

        {/* Methodology & sources */}
        <Section title="Methodology & Sources" kicker="How we built this">
          <p className="text-zinc-400 text-sm leading-relaxed mb-4">
            Benchmarks are compiled from publicly available 2025 HYROX race results and independent
            performance analyses, then continuously refined with anonymized finish-time predictions
            submitted by NinjAthlete athletes. Percentile rankings blend this public model with live
            submissions as our dataset grows. Times include RoxZone transition/walk time as recorded
            by official timing.
          </p>
          <ul className="space-y-2" data-testid="report-sources">
            {SOURCES.map((s, i) => (
              <li key={s.url} className="text-xs">
                <span className="text-zinc-600 font-mono mr-2">[{i + 1}]</span>
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-[#e8ff00] underline decoration-zinc-700">{s.name}</a>
              </li>
            ))}
          </ul>
          <p className="text-zinc-600 text-xs mt-6 border-t border-zinc-800 pt-4">
            Free to cite with attribution to NinjAthlete (ninjathlete.com). Data licensed CC BY 4.0.
          </p>
          <p className="text-zinc-500 text-sm mt-4">
            Want the full written analysis with world records, age-group medians and regional comparisons?
            {' '}
            <Link to="/blogs/article/hyrox-finish-time-benchmark-report-2026" className="text-[#e8ff00] hover:text-white underline decoration-zinc-700" data-testid="report-article-link">
              Read the complete benchmark breakdown →
            </Link>
          </p>
        </Section>
      </div>
    </main>
  );
}

function StatCard({ icon, value, label }) {
  return (
    <div className="bg-[#161616] border border-zinc-800 p-4 text-center">
      <span className="inline-flex items-center justify-center text-[#e8ff00] mb-1">{icon}</span>
      <span className="block font-heading text-2xl sm:text-3xl text-white">{value}</span>
      <span className="block font-mono text-[9px] tracking-wider text-zinc-500 uppercase mt-1">{label}</span>
    </div>
  );
}

function Section({ title, kicker, children }) {
  return (
    <section className="mb-14">
      <p className="font-mono text-[10px] tracking-[0.2em] text-[#e8ff00] uppercase mb-2">{kicker}</p>
      <h2 className="font-heading text-3xl sm:text-4xl text-white mb-5">{title}</h2>
      {children}
    </section>
  );
}
